import * as React from "react";
import {
  Box,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface FilterOption {
  id: number;
  name: string;
  count?: number;
}

export type PayerSelection = "all" | number;
export type PlanEligibilityValue = "plan_eligible" | "plan_ineligible";
export type ProgramEligibilityValue = "eligible" | "not_eligible";
export type EnrollmentValue = "enrolled" | "not_enrolled";

export interface PatientRosterFilters {
  payerId: PayerSelection;
  planEligibility: PlanEligibilityValue[];
  programEligibility: ProgramEligibilityValue[];
  programIds: number[];
  enrollment: EnrollmentValue[];
  excludeDoNotContact: boolean;
}

export const DEFAULT_PATIENT_ROSTER_FILTERS: PatientRosterFilters = {
  payerId: "all",
  planEligibility: [],
  programEligibility: [],
  programIds: [],
  enrollment: [],
  excludeDoNotContact: false,
};

export interface FilterOptionCounts {
  payers?: Partial<Record<number | "all", number>>;
  planEligibility?: Partial<Record<PlanEligibilityValue, number>>;
  programEligibility?: Partial<Record<ProgramEligibilityValue, number>>;
  programs?: Partial<Record<number, number>>;
  enrollment?: Partial<Record<EnrollmentValue, number>>;
  doNotContact?: number;
}

export interface PatientRosterAdvancedFiltersProps {
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  payerOptions: FilterOption[];
  programOptions: FilterOption[];
  filters: PatientRosterFilters;
  setFilters: React.Dispatch<React.SetStateAction<PatientRosterFilters>>;
  filterOptionCounts?: FilterOptionCounts;
  disabled: boolean;
}

const isFiltersDirty = (filters: PatientRosterFilters): boolean =>
  filters.payerId !== "all" ||
  filters.planEligibility.length > 0 ||
  filters.programEligibility.length > 0 ||
  filters.programIds.length > 0 ||
  filters.enrollment.length > 0 ||
  filters.excludeDoNotContact;

const renderCount = (count: number | undefined) => {
  if (count === undefined || count === null) return null;
  return (
    <span style={{ marginLeft: 4, color: "#A29D9B", fontWeight: 400 }}>
      ({count})
    </span>
  );
};

interface FilterSectionProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  expanded,
  onToggle,
  children,
}) => (
  <Accordion
    expanded={expanded}
    onChange={onToggle}
    sx={{
      boxShadow: "none",
      border: "none",
      marginTop: "12px",
      "&.Mui-expanded": { marginTop: "12px" },
      "&:before": { display: "none" },
    }}
  >
    <AccordionSummary
      expandIcon={null}
      sx={{
        padding: 0,
        minHeight: "20px",
        "&.MuiAccordionSummary-root": { minHeight: "20px" },
        "&.MuiAccordionSummary-gutters": {
          padding: 0,
          minHeight: "20px",
        },
        "&.Mui-expanded": { minHeight: "20px" },
        "& .MuiAccordionSummary-content": {
          margin: 0,
          alignItems: "center",
          minHeight: "20px",
        },
        "& .MuiAccordionSummary-contentGutters": {
          marginBottom: "10px",
          minHeight: "20px",
        },
        "& .MuiAccordionSummary-content.Mui-expanded": {
          margin: 0,
          minHeight: "20px",
          alignItems: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          p: "0 20px 0 12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        >
          <ArrowDropDownIcon />
        </Box>
        <p className="collapse-list-header">{title}</p>
      </Box>
    </AccordionSummary>
    <AccordionDetails
      sx={{
        padding: 0,
        backgroundColor: "#F8F8F8",
        p: "20px 20px 12px 16px",
      }}
    >
      {children}
    </AccordionDetails>
  </Accordion>
);

interface RadioOptionProps {
  label: string;
  selected: boolean;
  onSelect: () => void;
  count?: number;
  disabled?: boolean;
}

const RadioOption: React.FC<RadioOptionProps> = ({
  label,
  selected,
  onSelect,
  count,
  disabled,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: "12px" }}>
    <Radio
      size="small"
      checked={selected}
      onChange={onSelect}
      disabled={disabled}
      sx={{
        padding: 0,
        marginRight: "6px",
        color: "#A29D9B",
        "&.Mui-checked": { color: "#FF890A" },
        "& .MuiSvgIcon-root": { fontSize: 14 },
      }}
    />
    <Typography
      onClick={disabled ? undefined : onSelect}
      sx={{
        fontSize: 10,
        fontWeight: 500,
        color: "#1E1E1E",
        cursor: disabled ? "not-allowed" : "pointer",
        userSelect: "none",
      }}
    >
      {label}
      {renderCount(count)}
    </Typography>
  </Box>
);

interface CheckboxOptionProps {
  label: string;
  checked: boolean;
  onChange: () => void;
  count?: number;
  disabled?: boolean;
}

const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  label,
  checked,
  onChange,
  count,
  disabled,
}) => (
  <div className="multiple-checkbox" style={{ marginBottom: 0 }}>
    <div className="checkbox-row-container">
      <label className="option-text">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="checkbox"
        />
        <span>
          {label}
          {renderCount(count)}
        </span>
      </label>
    </div>
  </div>
);

const PatientRosterAdvancedFilters: React.FC<
  PatientRosterAdvancedFiltersProps
> = ({
  anchorEl,
  setAnchorEl,
  payerOptions,
  programOptions,
  filters,
  setFilters,
  filterOptionCounts,
  disabled,
}) => {
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const [expanded, setExpanded] = React.useState({
    payer: true,
    planEligibility: true,
    programEligibility: true,
    programs: true,
    enrollment: true,
    additional: true,
  });

  const toggleSection = (key: keyof typeof expanded) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const clearAllFilters = () => {
    setFilters(DEFAULT_PATIENT_ROSTER_FILTERS);
  };

  const dirty = isFiltersDirty(filters);

  const togglePlanEligibility = (value: PlanEligibilityValue) => {
    setFilters((prev) => ({
      ...prev,
      planEligibility: prev.planEligibility.includes(value)
        ? prev.planEligibility.filter((v) => v !== value)
        : [...prev.planEligibility, value],
    }));
  };

  const toggleProgramEligibility = (value: ProgramEligibilityValue) => {
    setFilters((prev) => ({
      ...prev,
      programEligibility: prev.programEligibility.includes(value)
        ? prev.programEligibility.filter((v) => v !== value)
        : [...prev.programEligibility, value],
    }));
  };

  const toggleProgram = (id: number) => {
    setFilters((prev) => ({
      ...prev,
      programIds: prev.programIds.includes(id)
        ? prev.programIds.filter((v) => v !== id)
        : [...prev.programIds, id],
    }));
  };

  const toggleEnrollment = (value: EnrollmentValue) => {
    setFilters((prev) => ({
      ...prev,
      enrollment: prev.enrollment.includes(value)
        ? prev.enrollment.filter((v) => v !== value)
        : [...prev.enrollment, value],
    }));
  };

  const toggleExcludeDoNotContact = () => {
    setFilters((prev) => ({
      ...prev,
      excludeDoNotContact: !prev.excludeDoNotContact,
    }));
  };

  const setPayer = (value: PayerSelection) => {
    setFilters((prev) => ({ ...prev, payerId: value }));
  };

  return (
    <Popover
      id="patient-roster-advanced-filters"
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        sx: {
          maxHeight: "calc(100vh - 160px)",
        },
      }}
    >
      <div
        className="popover-filter-container"
        style={{ height: "auto", overflowY: "auto" }}
      >
        <div className="header-wrapper">
          <div className="header">Filters</div>
          {dirty && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="clear-all"
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="7.99902"
                  width="8"
                  height="8"
                  rx="1"
                  transform="rotate(-90 0 7.99902)"
                  fill="#FF890A"
                />
                <path
                  d="M2.13699 1.66585L1.66699 2.13585L3.53033 3.99919L1.66699 5.86252L2.13699 6.33252L4.00033 4.46919L5.86366 6.33252L6.33366 5.86252L4.47033 3.99919L6.33366 2.13585L5.86366 1.66585L4.00033 3.52919L2.13699 1.66585Z"
                  fill="white"
                />
              </svg>
              <span>Clear All</span>
            </button>
          )}
        </div>

        <FilterSection
          title="Payer"
          expanded={expanded.payer}
          onToggle={() => toggleSection("payer")}
        >
          <RadioOption
            label="All Payers"
            selected={filters.payerId === "all"}
            onSelect={() => setPayer("all")}
            count={filterOptionCounts?.payers?.["all"]}
            disabled={disabled}
          />
          {payerOptions.map((opt) => (
            <RadioOption
              key={opt.id}
              label={opt.name}
              selected={filters.payerId === opt.id}
              onSelect={() => setPayer(opt.id)}
              count={filterOptionCounts?.payers?.[opt.id] ?? opt.count}
              disabled={disabled}
            />
          ))}
          {payerOptions.length === 0 && (
            <Typography sx={{ fontSize: 10, color: "#A29D9B" }}>
              No payers configured.
            </Typography>
          )}
        </FilterSection>

        <FilterSection
          title="Plan Eligibility"
          expanded={expanded.planEligibility}
          onToggle={() => toggleSection("planEligibility")}
        >
          <CheckboxOption
            label="Plan Eligible"
            checked={filters.planEligibility.includes("plan_eligible")}
            onChange={() => togglePlanEligibility("plan_eligible")}
            count={filterOptionCounts?.planEligibility?.plan_eligible}
            disabled={disabled}
          />
          <CheckboxOption
            label="Plan Ineligible"
            checked={filters.planEligibility.includes("plan_ineligible")}
            onChange={() => togglePlanEligibility("plan_ineligible")}
            count={filterOptionCounts?.planEligibility?.plan_ineligible}
            disabled={disabled}
          />
        </FilterSection>

        <FilterSection
          title="Program Eligibility"
          expanded={expanded.programEligibility}
          onToggle={() => toggleSection("programEligibility")}
        >
          <CheckboxOption
            label="Eligible"
            checked={filters.programEligibility.includes("eligible")}
            onChange={() => toggleProgramEligibility("eligible")}
            count={filterOptionCounts?.programEligibility?.eligible}
            disabled={disabled}
          />
          <CheckboxOption
            label="Not Eligible"
            checked={filters.programEligibility.includes("not_eligible")}
            onChange={() => toggleProgramEligibility("not_eligible")}
            count={filterOptionCounts?.programEligibility?.not_eligible}
            disabled={disabled}
          />
        </FilterSection>

        <FilterSection
          title="Programs"
          expanded={expanded.programs}
          onToggle={() => toggleSection("programs")}
        >
          {programOptions.map((opt) => (
            <CheckboxOption
              key={opt.id}
              label={opt.name}
              checked={filters.programIds.includes(opt.id)}
              onChange={() => toggleProgram(opt.id)}
              count={filterOptionCounts?.programs?.[opt.id] ?? opt.count}
              disabled={disabled}
            />
          ))}
          {programOptions.length === 0 && (
            <Typography sx={{ fontSize: 10, color: "#A29D9B" }}>
              No programs configured.
            </Typography>
          )}
        </FilterSection>

        <FilterSection
          title="Enrollment"
          expanded={expanded.enrollment}
          onToggle={() => toggleSection("enrollment")}
        >
          <CheckboxOption
            label="Not Enrolled"
            checked={filters.enrollment.includes("not_enrolled")}
            onChange={() => toggleEnrollment("not_enrolled")}
            count={filterOptionCounts?.enrollment?.not_enrolled}
            disabled={disabled}
          />
          <CheckboxOption
            label="Enrolled"
            checked={filters.enrollment.includes("enrolled")}
            onChange={() => toggleEnrollment("enrolled")}
            count={filterOptionCounts?.enrollment?.enrolled}
            disabled={disabled}
          />
        </FilterSection>

        <FilterSection
          title="Additional Filters"
          expanded={expanded.additional}
          onToggle={() => toggleSection("additional")}
        >
          <CheckboxOption
            label="Remove Patients with Do Not Contact"
            checked={filters.excludeDoNotContact}
            onChange={toggleExcludeDoNotContact}
            count={filterOptionCounts?.doNotContact}
            disabled={disabled}
          />
        </FilterSection>
      </div>
    </Popover>
  );
};

export default PatientRosterAdvancedFilters;
